import { useEffect } from 'react';
import type { RefObject } from 'react';

let ampCtx: AudioContext | null = null;
let ampStream: MediaStream | null = null;
let ampRaf = 0;

function stopAmplitudeTracking(
  pulseEl: HTMLDivElement | null,
  pulseOuterEl: HTMLDivElement | null,
): void {
  cancelAnimationFrame(ampRaf);
  ampCtx?.close().catch(() => {});
  ampCtx = null;
  ampStream?.getTracks().forEach((track) => track.stop());
  ampStream = null;
  if (pulseEl) {
    pulseEl.style.transform = '';
    pulseEl.style.opacity = '';
  }
  if (pulseOuterEl) {
    pulseOuterEl.style.transform = '';
    pulseOuterEl.style.opacity = '';
  }
}

async function startAmplitudeTracking(
  pulseEl: HTMLDivElement | null,
  pulseOuterEl: HTMLDivElement | null,
): Promise<void> {
  stopAmplitudeTracking(pulseEl, pulseOuterEl);
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const AudioCtx: typeof AudioContext = window.AudioContext ?? (window as any).webkitAudioContext;
    if (!AudioCtx) return;
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    if (!pulseEl || !pulseOuterEl) {
      stream.getTracks().forEach((track) => track.stop());
      return;
    }

    ampStream = stream;
    ampCtx = new AudioCtx();

    const analyser = ampCtx.createAnalyser();
    analyser.fftSize = 256;
    analyser.smoothingTimeConstant = 0.3;
    ampCtx.createMediaStreamSource(stream).connect(analyser);

    const buffer = new Uint8Array(analyser.frequencyBinCount);
    let smoothed = 0;

    const animate = () => {
      if (!pulseEl || !pulseOuterEl) return;

      analyser.getByteFrequencyData(buffer);
      const rms = Math.sqrt(buffer.reduce((sum, value) => sum + value * value, 0) / buffer.length);
      const normalized = Math.min(rms / 55, 1);
      smoothed = smoothed * 0.72 + normalized * 0.28;

      const now = Date.now();
      const progressA = (now % 1500) / 1500;
      const progressB = ((now + 600) % 1500) / 1500;
      const maxExpansion = 0.25 + smoothed * 1.15;
      const minVisibleAmp = 0.04;

      const scaleA = 1 + progressA * maxExpansion;
      const opacityA = smoothed > minVisibleAmp
        ? (1 - progressA) * Math.min(smoothed * 3.5, 0.75)
        : 0;
      const scaleB = 1 + progressB * maxExpansion * 0.82;
      const opacityB = smoothed > minVisibleAmp
        ? (1 - progressB) * Math.min(smoothed * 2.8, 0.55)
        : 0;

      pulseEl.style.transform = `scale(${scaleA.toFixed(3)})`;
      pulseEl.style.opacity = opacityA.toFixed(3);
      pulseOuterEl.style.transform = `scale(${scaleB.toFixed(3)})`;
      pulseOuterEl.style.opacity = opacityB.toFixed(3);

      ampRaf = requestAnimationFrame(animate);
    };

    ampRaf = requestAnimationFrame(animate);
  } catch {
    // Microphone access denied or unavailable: no amplitude visualization.
  }
}

interface UseDrivingModeAmplitudeArgs {
  listening: boolean;
  pulseRef: RefObject<HTMLDivElement | null>;
  pulseOuterRef: RefObject<HTMLDivElement | null>;
}

export function useDrivingModeAmplitude({
  listening,
  pulseRef,
  pulseOuterRef,
}: UseDrivingModeAmplitudeArgs): void {
  useEffect(() => {
    if (listening) {
      void startAmplitudeTracking(pulseRef.current, pulseOuterRef.current);
    } else {
      stopAmplitudeTracking(pulseRef.current, pulseOuterRef.current);
    }

    return () => {
      stopAmplitudeTracking(pulseRef.current, pulseOuterRef.current);
    };
  }, [listening, pulseOuterRef, pulseRef]);
}
